import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { FormTextarea } from '@/components/form/FormTextarea'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { useToast } from '@/components/ui/toastContext'
import {
  useOrganizationQuery,
  useUpdateOrganization,
  useUploadOrgLogo,
} from '@/features/recruiter/hooks/useOrganization'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'

const schema = z.object({
  name: z.string().min(1, 'Company name is required').max(255),
  legal_name: z.string().max(255).optional(),
  website: z.union([z.url('Enter a valid URL'), z.literal('')]).optional(),
  industry: z.string().max(100).optional(),
  size_range: z.string().max(50).optional(),
  description: z.string().max(2000).optional(),
})

type FormValues = z.infer<typeof schema>

export function OrganizationProfilePage() {
  const { data: organization, isPending } = useOrganizationQuery()
  const update = useUpdateOrganization()
  const uploadLogo = useUploadOrgLogo()
  const toast = useToast()
  const logoInputRef = useRef<HTMLInputElement>(null)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      legal_name: '',
      website: '',
      industry: '',
      size_range: '',
      description: '',
    },
  })

  useEffect(() => {
    if (!organization) return
    reset({
      name: organization.name,
      legal_name: organization.legalName ?? '',
      website: organization.website ?? '',
      industry: organization.industry ?? '',
      size_range: organization.sizeRange ?? '',
      description: organization.description ?? '',
    })
  }, [organization, reset])

  if (isPending || !organization) {
    return <p className="text-muted my-4">Loading organization…</p>
  }

  const onSubmit = handleSubmit((values) => {
    update.mutate({ ...values, website: values.website || undefined })
  })

  return (
    <>
      <div className="my-4 page-header-breadcrumb">
        <p className="fw-semibold fs-20 mb-0">Organization Profile</p>
      </div>

      <div className="row g-3">
        <div className="col-xl-4">
          <Card title="Logo">
            <div className="text-center">
              {organization.logoUrl ? (
                <img
                  src={organization.logoUrl}
                  alt={organization.name}
                  className="rounded mb-3"
                  style={{ maxWidth: 160 }}
                />
              ) : (
                <span className="avatar avatar-xl avatar-rounded bg-primary-transparent mb-3">
                  {organization.name.charAt(0).toUpperCase()}
                </span>
              )}
              <div>
                <Button
                  variant="secondary"
                  loading={uploadLogo.isPending}
                  onClick={() => logoInputRef.current?.click()}
                >
                  {organization.logoUrl ? 'Replace Logo' : 'Upload Logo'}
                </Button>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  className="d-none"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    uploadLogo.mutate(file, { onError: (err) => toast.error(getErrorMessage(err)) })
                    e.target.value = ''
                  }}
                />
              </div>
            </div>
          </Card>
        </div>

        <div className="col-xl-8">
          <Card>
            <FormErrorSummary
              errors={errors}
              serverErrors={getFieldErrors(update.error)}
              generalMessage={update.error ? getErrorMessage(update.error) : null}
            />
            <form onSubmit={(e) => void onSubmit(e)} noValidate>
              <FormInput control={control} name="name" label="Company Name" required />
              <FormInput control={control} name="legal_name" label="Legal Name" />
              <FormInput control={control} name="website" label="Website" type="url" />
              <div className="row">
                <div className="col-md-6">
                  <FormInput control={control} name="industry" label="Industry" />
                </div>
                <div className="col-md-6">
                  <FormInput
                    control={control}
                    name="size_range"
                    label="Company Size"
                    placeholder="e.g. 51-200"
                  />
                </div>
              </div>
              <FormTextarea
                control={control}
                name="description"
                label="Description"
                maxLength={2000}
              />
              <Button type="submit" loading={update.isPending}>
                Save
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </>
  )
}
